import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ExecutiveSummaryData } from './executive-summary';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import * as abac from '@lifeomic/abac';
import { enforce } from '../../../../util/abac';

const messages = defineMessages({
  status: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.status',
    defaultMessage: 'Status',
  },
  signOff: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOff',
    defaultMessage: 'Sign Off',
  },
  reopen: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.reopen',
    defaultMessage: 'Reopen',
  },
  discard: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.discard',
    defaultMessage: 'Discard',
  },
  signOffModal: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOffModal',
    defaultMessage:
      'Are you sure you want to sign off? This report and all referenced documents will be made available to the member.',
  },
  cancel: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.cancel',
    defaultMessage: 'Cancel',
  },
  completedButton: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.completedButton',
    defaultMessage: 'Sign Off',
  },
  activeButton: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.activeButton',
    defaultMessage: 'Reopen',
  },
  draftButton: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.draftButton',
    defaultMessage: 'Move to Draft',
  },
  'entered-in-errorButton': {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.enteredInErrorButton',
    defaultMessage: 'Discard',
  },
  completedModal: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.completedModal',
    defaultMessage:
      'Are you sure you want to sign off? This report and all referenced documents will be made available to the member.',
  },
  completedModal2: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.completedModal2',
    defaultMessage:
      'Once Signed Off, this report is final and a new Active report can be started.',
  },
  activeModal: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.activeModal',
    defaultMessage: 'Are you sure you want to reopen this report?',
  },
  'entered-in-errorModal': {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.enteredInErrorModal',
    defaultMessage: 'Are you sure you want to discard this report?',
  },
  reopenSignedOffButton: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.reopenSignedOffButton',
    defaultMessage: 'Reopen',
  },
  reopenSignedOffModalTitle: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.reopenSignedOffModalTitle',
    defaultMessage: 'Reopen signed-off report?',
  },
  reopenSignedOffModalWarning: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.reopenSignedOffModalWarning',
    defaultMessage:
      'This will move the report back to Active status. The system will attempt to retract it from the EHR export and revert any reports it auto-completed, but reports that were signed off as a side effect of this export cannot be un-signed via the EHR API and will remain final there.',
  },
  reopenSignedOffModalConfirm: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.reopenSignedOffModalConfirm',
    defaultMessage: 'Reopen report',
  },
  reopenBlockedByActivePlan: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.reopenBlockedByActivePlan',
    defaultMessage:
      "This report can't be reopened because another Active action plan already exists.",
  },
});

export type StatusValue =
  | 'draft'
  | 'active'
  | 'completed'
  | 'entered-in-error'
  | 'unknown';

export const Status = ({
  executiveSummary,
  onStatusChange,
  disabled,
  policy,
  hasOtherActiveCarePlan,
}: {
  executiveSummary: ExecutiveSummaryData;
  onStatusChange: (status: StatusValue) => void;
  disabled?: boolean;
  policy?: abac.AbacReducedPolicy;
  hasOtherActiveCarePlan?: boolean;
}) => {
  const { formatMessage } = useIntl();

  const [confirmingStatus, setConfirmingStatus] = useState<StatusValue | null>(
    null,
  );
  const [confirmingReopen, setConfirmingReopen] = useState(false);

  const confirmChange = () => {
    confirmingStatus && onStatusChange(confirmingStatus);
    setConfirmingStatus(null);
  };

  const confirmReopen = () => {
    onStatusChange('active');
    setConfirmingReopen(false);
  };

  if (executiveSummary.status === 'completed') {
    const canUnsign = enforce('arUnsign', policy ?? { rules: {} });
    if (!canUnsign) {
      return null;
    }

    if (hasOtherActiveCarePlan) {
      return <Text>{formatMessage(messages.reopenBlockedByActivePlan)}</Text>;
    }

    return (
      <>
        <Modal
          size={0}
          isOpen={confirmingReopen}
          onDismiss={() => setConfirmingReopen(false)}
          title={formatMessage(messages.reopenSignedOffModalTitle)}
          actions={[
            <Button
              key="cancel"
              variant="outlined"
              onClick={() => setConfirmingReopen(false)}
            >
              {formatMessage(messages.cancel)}
            </Button>,
            <Button key="reopen" onClick={confirmReopen}>
              {formatMessage(messages.reopenSignedOffModalConfirm)}
            </Button>,
          ]}
        >
          <Text>{formatMessage(messages.reopenSignedOffModalWarning)}</Text>
        </Modal>
        <Box direction="row" align="center" justify="space-evenly" gap={2}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setConfirmingReopen(true)}
            disabled={disabled}
          >
            {formatMessage(messages.reopenSignedOffButton)}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Modal
        size={0}
        isOpen={!!confirmingStatus}
        onDismiss={() => setConfirmingStatus(null)}
        title={formatMessage(messages.signOff)}
        actions={[
          <Button
            key="cancel"
            variant="outlined"
            onClick={() => setConfirmingStatus(null)}
          >
            {formatMessage(messages.cancel)}
          </Button>,
          <Button key="signOff" onClick={confirmChange}>
            {confirmingStatus &&
              formatMessage(
                messages[`${confirmingStatus}Button` as keyof typeof messages],
              )}
          </Button>,
        ]}
      >
        <Text>
          {confirmingStatus &&
            formatMessage(
              messages[`${confirmingStatus}Modal` as keyof typeof messages],
            )}
        </Text>
        {confirmingStatus &&
          messages[`${confirmingStatus}Modal2` as keyof typeof messages] && (
            <Text style={{ marginTop: 16 }}>
              {formatMessage(
                messages[`${confirmingStatus}Modal2` as keyof typeof messages],
              )}
            </Text>
          )}
      </Modal>
      <Box direction="row" align="center" justify="space-evenly" gap={2}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() =>
            setConfirmingStatus(
              executiveSummary.status === 'entered-in-error'
                ? 'active'
                : 'entered-in-error',
            )
          }
          disabled={disabled}
        >
          {executiveSummary.status === 'entered-in-error'
            ? formatMessage(messages.reopen)
            : formatMessage(messages.discard)}
        </Button>
        <Button
          fullWidth
          color="default"
          onClick={() => setConfirmingStatus('completed')}
          disabled={disabled || executiveSummary.status !== 'active'}
        >
          {formatMessage(messages.signOff)}
        </Button>
      </Box>
    </>
  );
};
